import { ModalOpenProcess } from "@msys/ui";
import { Edit as EditIcon } from "@mui/icons-material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Button } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { useUrlSearchParams } from "../../../commons/hooks/useUrlSearchParams.js";
import { buildDocPath } from "../../../utils.js";
import { QuoteTemplatePublishAndDiscardButton } from "./buttons/QuoteTemplatePublishAndDiscardButton.js";
import { QuoteTemplatePublishButton } from "./buttons/QuoteTemplatePublishButton.js";
import { QuoteTemplateSubscribeButton } from "./buttons/QuoteTemplateSubscribeButton.js";
import { QuoteTemplateUnsubscribeButton } from "./buttons/QuoteTemplateUnsubscribeButton.js";
import { QuoteTemplateUpdateSubscribedVersionButton } from "./buttons/QuoteTemplateUpdateSubscribedVersionButton.js";
import { ShowTemplateVariantProtocolModal } from "./ShowTemplateVariantProtocolModal.js";
import { TemplateDecisionExternalWizardPreview } from "./TemplateDecisionExternalWizardPreview.js";
import { TemplateQuoteAction_QuoteTemplateFragment } from "./TemplatesQuoteTemplateAction.generated.js";
import { useTemplateUpdateProtocol } from "./useTemplateUpdateProtocol.js";
import { useFetchUpdateProtocolEntries } from "./useTemplateVariantUpdateProtocol.js";

interface Props {
  doc: TemplateQuoteAction_QuoteTemplateFragment;
  pathToDoc: string;
  pathToDocList: string;
}

export const TemplatesQuoteTemplateAction = ({
  doc,
  pathToDoc,
  pathToDocList,
}: Props) => {
  const { t } = useTranslate(["Global", "Templates", "Decisions", "Quote"]);
  const viewer = useUserData().currentUser!;
  const navigate = useNavigate();
  const { urlSearchParams } = useUrlSearchParams();

  const navigateFrom = urlSearchParams.get("navigateFrom");
  const navigateReturnPath = urlSearchParams.get("navigateReturnPath");
  const canNavigateBackToQuote =
    navigateFrom === "quote" && Boolean(navigateReturnPath);
  const isOwnTemplate =
    doc.owningSystemOrganisationId === viewer.organisation.id;
  const isVersion = doc.resolvedAsReadModelVersionNumber !== null;
  const isShared = !!doc.templateSharing;
  const latestPublishedVersionNumber = doc.latestPublishedVersionNumber ?? null;
  const hasPublishedVersion = latestPublishedVersionNumber !== null;
  const isLatestPublishedVersion =
    latestPublishedVersionNumber !== null &&
    doc.resolvedAsReadModelVersionNumber === latestPublishedVersionNumber;
  const latestSharedVersionNumber =
    doc.templateSharing?.latestSharedVersionNumber ?? null;
  const isLatestSharedVersion =
    latestSharedVersionNumber !== null &&
    doc.resolvedAsReadModelVersionNumber === latestSharedVersionNumber;
  const subscribedVersion =
    doc.templateSharing?.subscribedToTemplate?.subscribedVersionNumber ?? null;
  const isSubscribedVersion =
    subscribedVersion !== null &&
    doc.resolvedAsReadModelVersionNumber === subscribedVersion;
  const showEditButton = isOwnTemplate && isVersion && isLatestPublishedVersion;
  const showSubscribedVersionButton =
    !isOwnTemplate && subscribedVersion !== null && !isSubscribedVersion;
  const showLatestPublishedVersionButton =
    isOwnTemplate && latestPublishedVersionNumber && !isLatestPublishedVersion;
  const showLatestSharedVersionButton =
    !isOwnTemplate &&
    latestSharedVersionNumber !== null &&
    !isLatestSharedVersion &&
    (!showSubscribedVersionButton ||
      subscribedVersion !== latestSharedVersionNumber);
  const canPublish =
    isOwnTemplate && !isVersion && doc.containsUnpublishedChanged;
  const canDiscardUnpublishedChanges = doc.hasAnyPublishedVersion && canPublish;
  const canSubscribe =
    !isOwnTemplate &&
    isShared &&
    subscribedVersion === null &&
    isLatestSharedVersion;
  const canUnsubscribe = !isOwnTemplate && isSubscribedVersion;
  const canUpdateSubscribedVersion =
    !isOwnTemplate &&
    doc.templateSharing?.subscribedToTemplate?.updateAvailable &&
    !isSubscribedVersion &&
    isLatestSharedVersion;

  const { checkUpdateProtocol, updateVersionProtocolProcessRef } =
    useFetchUpdateProtocolEntries();

  const hasOverridesDefined =
    useTemplateUpdateProtocol(doc.id)?.hasOverridesDefined ?? false;

  const actionButtons: React.ReactNode[] = doc
    ? [
        canNavigateBackToQuote && navigateReturnPath && (
          <RestrictedByCapabilityWithDebug
            capability="QUOTING"
            key={"return-back-to-quote"}
          >
            <Button
              component={Link}
              to={navigateReturnPath}
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
            >
              {t("Return to quote", { ns: "Quote" })}
            </Button>
          </RestrictedByCapabilityWithDebug>
        ),
        canPublish && !canDiscardUnpublishedChanges && (
          <RestrictedByOrganisationPermissionWithDebug
            permission="MANAGE_TEMPLATE"
            key={"publish-button"}
          >
            <QuoteTemplatePublishButton docId={doc.id} pathToDoc={pathToDoc} />
          </RestrictedByOrganisationPermissionWithDebug>
        ),
        canPublish && canDiscardUnpublishedChanges && (
          <RestrictedByOrganisationPermissionWithDebug
            permission="MANAGE_TEMPLATE"
            key={"publish-and-discard-button"}
          >
            <QuoteTemplatePublishAndDiscardButton
              docId={doc.id}
              pathToDoc={pathToDoc}
            />
          </RestrictedByOrganisationPermissionWithDebug>
        ),
        showEditButton && (
          <RestrictedByOrganisationPermissionWithDebug
            permission="MANAGE_TEMPLATE"
            key={"edit-button"}
          >
            <Button
              component={Link}
              to={buildDocPath(pathToDoc, null)}
              variant="contained"
              startIcon={<EditIcon />}
            >
              {t("Edit", { ns: "Global" })}
            </Button>
          </RestrictedByOrganisationPermissionWithDebug>
        ),
        showLatestPublishedVersionButton && (
          <Button
            key={"see-latest"}
            component={Link}
            to={buildDocPath(pathToDoc, latestPublishedVersionNumber)}
            variant="outlined"
            startIcon={<VisibilityIcon />}
          >
            {t("See latest version", { ns: "Templates" })}
          </Button>
        ),
        showLatestSharedVersionButton && (
          <Button
            key={"see-latest"}
            component={Link}
            to={buildDocPath(pathToDoc, latestSharedVersionNumber)}
            variant="outlined"
            startIcon={<VisibilityIcon />}
          >
            {t("See latest version", { ns: "Templates" })}
          </Button>
        ),
        showSubscribedVersionButton && (
          <Button
            key={"see-subscribed"}
            component={Link}
            to={buildDocPath(pathToDoc, subscribedVersion)}
            variant="outlined"
            startIcon={<VisibilityIcon />}
          >
            {t("See subscribed version", { ns: "Templates" })}
          </Button>
        ),
        canSubscribe && (
          <RestrictedByOrganisationPermissionWithDebug
            permission="SHARE_TEMPLATE"
            key={"subscribe-button"}
          >
            <QuoteTemplateSubscribeButton
              doc={doc}
              onAfterUpdate={async (opts, applyOverrides) => {
                if (applyOverrides) {
                  await checkUpdateProtocol(opts);
                }
              }}
            />
          </RestrictedByOrganisationPermissionWithDebug>
        ),
        canUnsubscribe && (
          <RestrictedByOrganisationPermissionWithDebug
            permission="SHARE_TEMPLATE"
            key={"subscribe-button"}
          >
            <QuoteTemplateUnsubscribeButton
              doc={doc}
              navigateToLatestSharedVersion={versionNumber => {
                navigate(buildDocPath(pathToDoc, versionNumber), {
                  replace: true,
                });
              }}
            />
          </RestrictedByOrganisationPermissionWithDebug>
        ),

        canUpdateSubscribedVersion && (
          <RestrictedByOrganisationPermissionWithDebug
            permission="MANAGE_TEMPLATE"
            key={"update-subscribed-version"}
          >
            <QuoteTemplateUpdateSubscribedVersionButton
              doc={doc}
              pathToDoc={pathToDoc}
              // when overrides defined in template - we need to call subscribed version update before refetching all queries
              // when not defined - just after everything
              onBeforeUpdate={
                hasOverridesDefined ? checkUpdateProtocol : undefined
              }
            />
          </RestrictedByOrganisationPermissionWithDebug>
        ),
        (isShared || isOwnTemplate) && !canNavigateBackToQuote && (
          <TemplateDecisionExternalWizardPreview
            key="wizard-external-preview-button"
            templateId={doc.id}
            sourceDocType={
              !hasPublishedVersion
                ? "template-unpublished"
                : isOwnTemplate
                  ? undefined
                  : canSubscribe || canUpdateSubscribedVersion
                    ? "template-latest-available"
                    : "template"
            }
          />
        ),
        <ModalOpenProcess
          key="update-version-protocol-modal"
          ref={updateVersionProtocolProcessRef}
          Modal={ShowTemplateVariantProtocolModal}
          modalProps={{
            pathToDocList,
            toVersionNumber: doc.latestPublishedVersionNumber ?? 0,
            entries: [],
          }}
        />,
      ].filter(Boolean)
    : [];

  return actionButtons;
};
